import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Eyebrow } from '@/components/site/primitives'
import { useLocale } from '@/i18n/use-locale'
import { siteHome } from '@/i18n/site-home'

interface MonogramProps {
    className?: string
    tone?: 'light' | 'dark'
}

/** The founder's monogram, used in place of a photo. */
export const Monogram: FC<MonogramProps> = ({ className, tone = 'dark' }): ReactNode => {
    const dark = tone === 'dark'

    return (
        <div
            className={cn(
                'relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-full border',
                dark ? 'border-white/15 bg-ink-soft text-white' : 'border-border bg-white text-ink',
                className
            )}
            aria-hidden='true'
        >
            <div className={cn('absolute inset-0 mask-radial', dark ? 'bg-grid-dark' : 'bg-grid')} />
            <div className={cn('absolute inset-[9%] rounded-full border', dark ? 'border-white/10' : 'border-border')} />
            <div className='absolute -right-1/4 -top-1/4 size-3/4 rounded-full bg-primary/25 blur-3xl' />

            <span className='relative font-display text-[clamp(4rem,9vw,6.5rem)] leading-none tracking-[-0.02em]'>
                QU
            </span>

            <span className='absolute bottom-[14%] size-2.5 rounded-full bg-signal' />
        </div>
    )
}

/** Home page band: a short statement in the founder's own voice, attributed as such. */
export const FounderBand: FC = (): ReactNode => {
    const locale = useLocale()
    const copy = siteHome[locale].founder

    return (
        <section id='founder' className='relative overflow-hidden bg-ink py-24 text-white'>
            <div className='bg-grid-dark mask-fade-b pointer-events-none absolute inset-0 opacity-40' aria-hidden='true' />

            <div className='container-page relative grid gap-12 lg:grid-cols-[300px_1fr] lg:items-center lg:gap-20'>
                <div className='mx-auto w-full max-w-[260px] lg:max-w-none'>
                    <Monogram />

                    <div className='mt-6 text-center lg:text-left'>
                        <p className='text-lg font-semibold'>{copy.name}</p>
                        <p className='text-sm text-white/60'>{copy.role}</p>
                    </div>

                    <ul className='mt-5 flex flex-wrap justify-center gap-2 lg:justify-start'>
                        {copy.chips.map(chip => (
                            <li key={chip} className='rounded-full border border-white/15 px-3 py-1 text-xs text-white/75'>
                                {chip}
                            </li>
                        ))}
                    </ul>
                </div>

                <figure>
                    <Eyebrow tone='dark'>{copy.eyebrow}</Eyebrow>

                    <blockquote className='mt-6 space-y-6'>
                        {copy.statement.map((paragraph, index) => (
                            <p key={paragraph} className={cn('font-display leading-[1.2] tracking-[-0.005em]', index === 0 ? 'text-3xl sm:text-[2.6rem]' : 'text-2xl text-white/75 sm:text-3xl')}>
                                {paragraph}
                            </p>
                        ))}
                    </blockquote>

                    <figcaption className='mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6'>
                        <span className='text-sm text-white/70'>
                            {`${copy.name}, ${copy.role}`}
                        </span>

                        <Link href='/about#founder' className='group inline-flex items-center gap-1.5 text-sm font-medium text-white hover:text-signal'>
                            {copy.link}
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>
                    </figcaption>
                </figure>
            </div>
        </section>
    )
}
